import * as React from 'react';

import { Button } from '../button/button.tsx';
import { Hint } from '../hint/hint.tsx';
import { Icon } from '../icon/icon.tsx';

import './overview-card.css';

type OverviewCardSection =
  | 'application'
  | 'questions'
  | 'signature'
  | 'authorization'
  | 'review-and-submit'
  | 'missing-item';

/** Business-approved blurb telling the provider what a step asks them for. */
const sectionDescriptionRegistry: Partial<Record<OverviewCardSection, string>> =
  {
    application:
      'Enter provider demographics, education, licensure, work history, and professional qualifications.',
    questions:
      'Complete required disclosures regarding licensure, sanctions, malpractice history, and other professional matters.',
    signature:
      'Authorize verification of credentialing information and certify that all information provided is accurate.',
    authorization:
      'Grant permission for the health plan to review, use, and verify credentialing information for participation consideration.',
    'review-and-submit':
      'Review all information for accuracy and submit the application for processing.',
  };

interface OverviewCardProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onClick'> {
  state: 'error' | 'warning' | 'default' | 'disabled';
  started?: boolean;
  stateText?: string;
  progress: number;
  progressText?: string;
  section: OverviewCardSection;
  /** Overrides the registry label for the section (e.g. a missing item form name). */
  sectionName?: string;
  /** Position of this section in the overall form flow, shown as "Step {n} - {status}". */
  stepNumber?: number;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  isProvider?: boolean;
  isInviteSent?: boolean;
}

const OverviewCard = React.forwardRef<HTMLDivElement, OverviewCardProps>(
  (
    {
      progress,
      progressText,
      state,
      stateText,
      section,
      sectionName,
      stepNumber,
      onClick,
      isProvider = true,
      isInviteSent = false,
      started = false,
      ...props
    },
    ref,
  ) => {
    const description = sectionDescriptionRegistry[section];

    const statusTitleRegistry = [
      'Not Started Yet',
      'In Progress',
      'Completed',
      'Pending',
    ];
    const buttonTextRegistry = [
      'Start',
      'Continue',
      'Review',
      'Ask the Provider',
      'Resend Invite',
    ];

    const sectionNameRegistry = {
      application: 'Application',
      questions: 'Questions',
      signature: 'Signature',
      authorization: 'Authorization',
      'review-and-submit': 'Review and Submit',
      'missing-item': 'Missing Item',
    };
    const stateRegistry = {
      default: 'default',
      error: 'error',
      warning: 'warning',
      disabled: 'disabled',
    };

    const name = sectionName ?? sectionNameRegistry[section];

    let statusTitle: string = statusTitleRegistry[0];
    let buttonText: string = buttonTextRegistry[0];

    if (progress === 0) {
      statusTitle = started ? statusTitleRegistry[1] : statusTitleRegistry[0];
      buttonText = !isProvider
        ? isInviteSent
          ? buttonTextRegistry[4]
          : buttonTextRegistry[3]
        : buttonTextRegistry[0];
    } else if (progress > 0 && progress < 100) {
      statusTitle = statusTitleRegistry[1];
      buttonText = !isProvider
        ? isInviteSent
          ? buttonTextRegistry[4]
          : buttonTextRegistry[3]
        : buttonTextRegistry[1];
    } else if (progress === 100) {
      statusTitle = statusTitleRegistry[2];
      buttonText = !isProvider
        ? isInviteSent
          ? buttonTextRegistry[4]
          : buttonTextRegistry[3]
        : buttonTextRegistry[2];
    }
    if (state === 'disabled') statusTitle = statusTitleRegistry[3];

    return (
      <div
        ref={ref}
        className={`overview-card ${
          state === 'disabled'
            ? 'overview-card-disabled'
            : isProvider
            ? stateRegistry[state]
            : ''
        }`}
        {...props}
      >
        {state !== 'disabled' && (
          <progress
            className={`progress ${progress === 100 && 'progress-completed'}`}
            max="100"
            value={progress}
          />
        )}
        <div className="content">
          <div
            className={`status-title ${
              progress === 100 && 'status-title-completed'
            }`}
          >
            {stepNumber ? `Step ${stepNumber} - ${statusTitle}` : statusTitle}
          </div>
          <hr />
          <div
            className="status-section"
            style={{
              marginTop:
                !progressText && state === 'default' && isProvider
                  ? '15px'
                  : '',
            }}
          >
            {description ? (
              <span className="inline-flex items-center gap-1 whitespace-nowrap">
                {name}
                <Hint title={name} text={description} align="center" />
              </span>
            ) : (
              name
            )}
          </div>
          {(progressText || !isProvider) && (
            <div className="status-text">
              {!isProvider
                ? 'You need to ask the Provider to continue this step.'
                : progressText}
            </div>
          )}
          {state === 'error' && (
            <div className="state state-error">
              <Icon type="error" size="small" />
              <span>{stateText}</span>
            </div>
          )}
          {state === 'warning' && (
            <div className="state state-warning">
              <Icon type="warning" size="small" />
              <span>{stateText}</span>
            </div>
          )}
          {state === 'disabled' && (
            <div
              className="state state-disabled"
              style={{
                marginTop: 'auto',
                marginBottom: state === 'disabled' ? '0' : '30px',
              }}
            >
              <span>{stateText}</span>
            </div>
          )}
          {state !== 'disabled' && (
            <Button
              variant="secondary"
              kind="default"
              size="small"
              style={{
                width: 200,
                marginTop: isProvider
                  ? state === 'default' && !progressText
                    ? '40px'
                    : state === 'default' && progressText
                    ? 'auto'
                    : '36px'
                  : state === 'default' && !progressText
                  ? '10px'
                  : '0',
              }}
              children={buttonText}
              onClick={onClick}
            />
          )}
        </div>
      </div>
    );
  },
);

export { OverviewCard };
